/**
 * TelegramIntegration — Carbon tracking via Telegram Bot
 *
 * Connection flow:
 * 1. User clicks "Connect Telegram" → opens t.me/<bot>?start=<userId> in new tab
 * 2. Frontend polls Supabase `telegram_settings` every 3 s (up to 2 min)
 * 3. When the bot receives /start <userId> it writes a row; polling detects it
 * 4. UI transitions to "Connected" state showing @username + settings
 */

import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useToast } from "../../hooks/use-toast";
import {
  TelegramBotService,
  TelegramSettings,
} from "../../services/telegramBotService";
import TelegramTester from "./TelegramTester";
import {
  Send,
  CheckCircle,
  LinkIcon,
  Zap,
  Bot,
  FlaskConical,
  Bell,
  BellOff,
  Unlink,
  RefreshCw,
} from "lucide-react";

interface TelegramIntegrationProps {
  userId: string;
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000; // 2 minutes

const TelegramIntegration: React.FC<TelegramIntegrationProps> = ({
  userId,
}) => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<TelegramSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [pollSecondsLeft, setPollSecondsLeft] = useState(0);
  const [showTester, setShowTester] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    TelegramBotService.fetchSettings(userId).then((data) => {
      setSettings(data);
      setIsLoading(false);
    });
    return () => stopPolling();
  }, [userId]);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setIsPolling(false);
  };

  const startPolling = () => {
    setIsPolling(true);
    setPollSecondsLeft(POLL_TIMEOUT_MS / 1000);

    // Countdown display
    countdownRef.current = setInterval(() => {
      setPollSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);

    // Poll Supabase for the bot to have written the row
    pollRef.current = setInterval(async () => {
      const data = await TelegramBotService.fetchSettings(userId);
      if (data?.is_connected) {
        stopPolling();
        setSettings(data);
        toast({
          title: "🎉 Telegram Connected!",
          description: `Linked to ${data.telegram_username ? "@" + data.telegram_username : (data.telegram_first_name ?? "your account")}`,
        });
      }
    }, POLL_INTERVAL_MS);

    // Timeout
    timeoutRef.current = setTimeout(() => {
      stopPolling();
      toast({
        title: "Connection timed out",
        description:
          "Didn't detect the bot link. Open the link again and send /start to the bot.",
        variant: "destructive",
      });
    }, POLL_TIMEOUT_MS);
  };

  const handleConnect = () => {
    const link = TelegramBotService.getConnectLink(userId);
    window.open(link, "_blank", "noopener,noreferrer");
    startPolling();
  };

  const handleDisconnect = async () => {
    try {
      await TelegramBotService.disconnect(userId);
      setSettings(null);
      toast({
        title: "Disconnected",
        description: "Telegram integration removed.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to disconnect.",
        variant: "destructive",
      });
    }
  };

  const handleToggle = async (
    field: "notifications_enabled" | "daily_reminders",
    value: boolean,
  ) => {
    try {
      await TelegramBotService.updateSetting(userId, field, value);
      setSettings((prev) => (prev ? { ...prev, [field]: value } : prev));
    } catch {
      toast({
        title: "Error",
        description: "Could not update setting.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40e0d0]" />
        </CardContent>
      </Card>
    );
  }

  const isConnected = !!settings?.is_connected;
  const displayName = settings?.telegram_username
    ? "@" + settings.telegram_username
    : (settings?.telegram_first_name ?? "Your account");

  return (
    <div className="h-full overflow-hidden">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <div className="p-2 bg-[#229ED9]/10 rounded-lg">
          <Send className="h-6 w-6 text-[#229ED9]" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Telegram Carbon Tracking</h2>
          <p className="text-sm text-muted-foreground">
            Log your carbon footprint by messaging the Myhu bot
          </p>
        </div>
        {isConnected && (
          <Badge className="ml-auto bg-green-100 text-green-800 border-green-300">
            <CheckCircle className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        )}
      </div>

      <div className="h-[calc(100vh-200px)] overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6">
          {/* Left — Connection card */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bot className="h-5 w-5 text-[#229ED9]" />
                  Bot Connection
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isConnected ? (
                  <>
                    {isPolling ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-[#229ED9]/5 border border-[#229ED9]/20 rounded-lg">
                          <RefreshCw className="h-5 w-5 text-[#229ED9] animate-spin flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">
                              Waiting for you to start the bot…
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Open Telegram, find the bot, and tap Start (or
                              send{" "}
                              <code className="bg-muted px-1 rounded">
                                /start
                              </code>
                              ).
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-center text-muted-foreground">
                          Checking automatically — {pollSecondsLeft}s remaining
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              const link =
                                TelegramBotService.getConnectLink(userId);
                              window.open(
                                link,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }}
                          >
                            <LinkIcon className="h-4 w-4 mr-2" />
                            Reopen Bot Link
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={stopPolling}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Click the button below to open the Myhu Telegram bot.
                          Tap <strong>Start</strong> in Telegram and your
                          account will link automatically.
                        </p>
                        <Button
                          onClick={handleConnect}
                          className="w-full bg-[#229ED9] hover:bg-[#1a8bbf] text-white"
                          size="sm"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Connect Telegram
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-green-800 dark:text-green-200 text-sm">
                          {displayName}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-300">
                          Send messages to the bot to log your footprint
                        </p>
                      </div>
                    </div>

                    {/* Settings */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Notifications</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm flex items-center gap-2">
                            <Bell className="h-3.5 w-3.5" />
                            Daily reminders
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleToggle(
                                "daily_reminders",
                                !settings?.daily_reminders,
                              )
                            }
                            className={
                              settings?.daily_reminders
                                ? "text-green-600"
                                : "text-muted-foreground"
                            }
                          >
                            {settings?.daily_reminders ? "ON" : "OFF"}
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm flex items-center gap-2">
                            <BellOff className="h-3.5 w-3.5" />
                            Weekly summary
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleToggle(
                                "notifications_enabled",
                                !settings?.notifications_enabled,
                              )
                            }
                            className={
                              settings?.notifications_enabled
                                ? "text-green-600"
                                : "text-muted-foreground"
                            }
                          >
                            {settings?.notifications_enabled ? "ON" : "OFF"}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-destructive hover:text-destructive"
                      onClick={handleDisconnect}
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnect Telegram
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* How it works */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  How it Works
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  {
                    n: 1,
                    title: "Connect the Bot",
                    body: 'Tap "Connect Telegram" above — no phone number or approval required.',
                  },
                  {
                    n: 2,
                    title: "Send a Message",
                    body: 'Text natural-language entries like "tracked: drove 25 km, ate vegan".',
                  },
                  {
                    n: 3,
                    title: "AI Parses It",
                    body: "The bot calculates CO₂ emissions and replies with a summary.",
                  },
                  {
                    n: 4,
                    title: "Dashboard Updates",
                    body: "Your carbon dashboard reflects the new entry instantly.",
                  },
                ].map(({ n, title, body }) => (
                  <div key={n} className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-[#40e0d0]/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-[#40e0d0]">
                        {n}
                      </span>
                    </div>
                    <div>
                      <h5 className="font-medium text-sm">{title}</h5>
                      <p className="text-xs text-muted-foreground">{body}</p>
                    </div>
                  </div>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => setShowExamples((v) => !v)}
                >
                  {showExamples ? "Hide" : "Show"} Example Messages
                </Button>

                {showExamples && (
                  <div className="p-3 bg-muted/50 rounded-lg space-y-1 text-xs font-mono">
                    {[
                      '"Track carbon: drove 25km today"',
                      '"Carbon log: electricity 45 kWh, gas 30 kWh"',
                      '"Footprint: 2 servings meat, spent $50 shopping"',
                      '"Track emissions: flight 500km"',
                      '"Carbon: walked 5km, ate vegan lunch"',
                    ].map((ex) => (
                      <p
                        key={ex}
                        className="text-green-700 dark:text-green-400"
                      >
                        {ex}
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right — Benefits + Tester toggle */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Why Telegram?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {[
                    [
                      "⚡",
                      "Instant setup — no business approval or phone verification",
                    ],
                    [
                      "🌍",
                      "950M+ users globally, strong in EU, SE Asia, LATAM & Middle East",
                    ],
                    ["🤖", "First-class Bot API — free, documented, reliable"],
                    ["🔒", "End-to-end encrypted; no data stored at Telegram"],
                    [
                      "🔔",
                      "Optional daily reminders push directly to your chat",
                    ],
                    [
                      "📊",
                      "AI parses free-text messages — no rigid format required",
                    ],
                  ].map(([icon, text]) => (
                    <div key={text} className="flex items-start gap-2">
                      <Badge
                        variant="outline"
                        className="text-xs flex-shrink-0"
                      >
                        {icon}
                      </Badge>
                      <span className="text-xs">{text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-blue-500" />
                  Parser Sandbox
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Test how the bot will parse your messages before connecting.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowTester((v) => !v)}
                >
                  {showTester ? "Hide" : "Open"} Sandbox
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {showTester && (
          <div className="mt-2 pb-6">
            <TelegramTester />
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramIntegration;
